import { useCallback } from "react";

import { useRightPanelStore } from "../rightPanelStore";
import type { RightPanelKind } from "../rightPanelSurface";

/**
 * A draft's right-panel actions, bound to that draft's key.
 *
 * Every action needs the same key and nothing else, so binding them once is
 * both less repetition and a guarantee: a call site cannot mutate another
 * thread's panel, because it never sees a key to pass.
 *
 * `select` accepts any kind and honours only `preview`. Browser is the one
 * surface a draft can host — the rest need a persisted server thread, which a
 * draft does not have yet — and the launcher offers what the panel supports
 * rather than what this route supports.
 */
export function useDraftRightPanelControls(draftPanelKey: string) {
  const close = useCallback(() => {
    useRightPanelStore.getState().close(draftPanelKey);
  }, [draftPanelKey]);

  const select = useCallback(
    (kind: RightPanelKind) => {
      if (kind !== "preview") return;
      useRightPanelStore.getState().revealBrowser(draftPanelKey);
    },
    [draftPanelKey],
  );

  const activateSurface = useCallback(
    (surfaceId: string) => {
      useRightPanelStore.getState().activateSurface(draftPanelKey, surfaceId);
    },
    [draftPanelKey],
  );

  const closeSurface = useCallback(
    (surfaceId: string) => {
      useRightPanelStore.getState().closeSurface(draftPanelKey, surfaceId);
    },
    [draftPanelKey],
  );

  return { close, select, activateSurface, closeSurface };
}
